/**
 * career-runs — the KI-Läufe surface (route `/runs`, §8 / ADR-018). An owner-scoped,
 * COLD-READ projection of the per-user `run.Captured` stream: every AI call disclosed
 * turn-by-turn. A figure ledger, a PER-MODEL rollup (which model ran how often, how
 * fast, at what cost), then a two-level native `<details>` disclosure — each Run
 * (activity + when + credits) → each RunStep (model / engine, token metrics, the
 * params, the prompt messages and the model's raw reply, each with a copy affordance
 * and an honest "gekürzt" note when the size guard clipped it). Read-only; no store,
 * no push — a single `/api/runs` fetch (newest-first from the server).
 *
 * MONEY (D5): the user is billed in CREDITS, so credits are what the surface shows.
 * `costUsd` — the raw provider price, our cost BASIS before margin — reaches the wire
 * ONLY for a caller with the operator capability, and the server omits it for everyone
 * else (`api-v2/src/aggregates/runs.ts`). `providerCostVisible` merely tells the surface
 * which of the two it may render; it is never the boundary.
 *
 * LAYOUT (§0 "seamless document over cards"): this surface used to be boxes nested four
 * deep — a bordered run card, holding a filled step panel, holding a bordered step card,
 * holding a bordered cost box and bordered prompt boxes. Structure now comes from
 * typography, spacing and hairline rules. The only surviving containment is the scrolling
 * `<pre>` of a prompt/reply — a genuine affordance, not decoration.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import { seamAutoGrow } from "@platform/rlhf-elements"
import { skeletonRows } from "../lib/skeleton"
import {
  costSplit,
  engineChip,
  fmtEur,
  fmtNum,
  fmtRate,
  fmtWhen,
  paramGrid,
  promptBlock,
  tokenBar,
  type RunEngine,
  type RunParams,
  type RunPromptMsg,
  type RunStepView,
} from "../lib/run-trace"
import { I18nToken, apiGet, apiPost } from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"

// ─── Client view types (parsed from the untrusted wire record) ──────────────────
// The STEP shapes (`RunStepView`, `RunPromptMsg`, `RunParams`, `RunEngine`) live in
// `../lib/run-trace` beside the furniture that renders them; only the run wrapper —
// which is this surface's own read — is local.

export interface RunView {
  id: string
  activityType?: string
  steps: RunStepView[]
  createdAt: string
  /** The integer credits actually debited for this run (margin + per-action floor applied) —
   *  which is why it is NOT the sum of the steps' provider cost. */
  credits: number
}

const num = (v: unknown): number | undefined => (typeof v === "number" ? v : undefined)
const str = (v: unknown): string | undefined => (typeof v === "string" ? v : undefined)

function parseParams(raw: unknown): RunParams | undefined {
  if (!raw || typeof raw !== "object") return undefined
  const p = raw as Record<string, unknown>
  const out: RunParams = {
    ...(num(p["temperature"]) !== undefined ? { temperature: num(p["temperature"]) } : {}),
    ...(num(p["topP"]) !== undefined ? { topP: num(p["topP"]) } : {}),
    ...(num(p["maxTokens"]) !== undefined ? { maxTokens: num(p["maxTokens"]) } : {}),
    ...(str(p["reasoningEffort"]) ? { reasoningEffort: str(p["reasoningEffort"]) } : {}),
    ...(str(p["jsonSchemaName"]) ? { jsonSchemaName: str(p["jsonSchemaName"]) } : {}),
    ...(typeof p["jsonSchemaStrict"] === "boolean" ? { jsonSchemaStrict: p["jsonSchemaStrict"] } : {}),
  }
  return Object.keys(out).length > 0 ? out : undefined
}

function parseStep(raw: unknown): RunStepView | null {
  if (!raw || typeof raw !== "object") return null
  const s = raw as Record<string, unknown>
  const model = str(s["model"])
  const engine = str(s["engine"]) as RunEngine | undefined
  if (!model || !engine) return null
  const prompt = Array.isArray(s["prompt"])
    ? (s["prompt"] as unknown[]).flatMap((m): RunPromptMsg[] => {
        const mm = (m ?? {}) as Record<string, unknown>
        const role = str(mm["role"])
        const content = str(mm["content"])
        return role !== undefined && content !== undefined ? [{ role, content }] : []
      })
    : undefined
  return {
    model,
    engine,
    ...(num(s["promptTokens"]) !== undefined ? { promptTokens: num(s["promptTokens"]) } : {}),
    ...(num(s["completionTokens"]) !== undefined ? { completionTokens: num(s["completionTokens"]) } : {}),
    ...(num(s["cachedTokens"]) !== undefined ? { cachedTokens: num(s["cachedTokens"]) } : {}),
    ...(num(s["reasoningTokens"]) !== undefined ? { reasoningTokens: num(s["reasoningTokens"]) } : {}),
    ...(num(s["costUsd"]) !== undefined ? { costUsd: num(s["costUsd"]) } : {}),
    ...(num(s["latencyMs"]) !== undefined ? { latencyMs: num(s["latencyMs"]) } : {}),
    ...(typeof s["cacheHit"] === "boolean" ? { cacheHit: s["cacheHit"] } : {}),
    ...(str(s["label"]) ? { label: str(s["label"]) } : {}),
    ...(str(s["purpose"]) ? { purpose: str(s["purpose"]) } : {}),
    // The prompt-artifact version ("<seam>@<n>"), optional on the wire (contract-v2 `RunStepView`
    // derives it from the domain `RunStep`, where it is `"promptRef?": "string"`). It is the join
    // key from this step to the process-graph node that owns the prompt — dropped here, that edge
    // cannot be drawn at all.
    ...(str(s["promptRef"]) ? { promptRef: str(s["promptRef"]) } : {}),
    ...(prompt && prompt.length > 0 ? { prompt } : {}),
    ...(s["promptTruncated"] === true ? { promptTruncated: true } : {}),
    ...(str(s["completion"]) ? { completion: str(s["completion"]) } : {}),
    ...(s["completionTruncated"] === true ? { completionTruncated: true } : {}),
    ...(parseParams(s["params"]) ? { params: parseParams(s["params"]) } : {}),
    ...(str(s["contextNote"]) ? { contextNote: str(s["contextNote"]) } : {}),
    // The review marks. Parsed HERE or they are invisible forever: this client parse is the last
    // narrowing point on the read path, and an edited call that renders as an ordinary one is
    // exactly the dishonest trace the gate exists to prevent.
    ...(s["reviewed"] === true ? { reviewed: true } : {}),
    ...(s["reviewEdited"] === true ? { reviewEdited: true } : {}),
  }
}

/**
 * The `/api/runs` wire record → the client view. EXPORTED because the process-graph console
 * (`career-processes`) reads the SAME cold `/api/runs` payload to join real traces onto a graph
 * node: a second parse of the same wire shape would be a second place for a field to be dropped
 * (which is exactly how `promptRef` — the node↔step join key — went missing once already).
 */
export function parseRun(raw: unknown): RunView | null {
  if (!raw || typeof raw !== "object") return null
  const r = raw as Record<string, unknown>
  const id = str(r["id"])
  const createdAt = str(r["createdAt"])
  if (!id || !createdAt) return null
  const steps = Array.isArray(r["steps"]) ? (r["steps"] as unknown[]).map(parseStep).filter((s): s is RunStepView => s !== null) : []
  return { id, ...(str(r["activityType"]) ? { activityType: str(r["activityType"]) } : {}), steps, createdAt, credits: num(r["credits"]) ?? 0 }
}

// ─── Vocabulary (activity + turn labels; raw code token as fallback) ───────────
// Engine + message-role vocabulary lives in `../lib/run-trace` with its chips.

/** Turn labels emitted by the capture seams → German chips (raw token when unmapped). */
const LABEL_KEY: Record<string, CareerKey> = {
  extract: "runs.label.extract",
  verify: "runs.label.verify",
  match: "runs.label.match",
  "org-research": "runs.label.orgResearch",
  "org-summary": "runs.label.orgSummary",
  "opportunity-brief": "runs.label.brief",
  triage: "runs.label.triage",
  "discovery-triage": "runs.label.triage",
  preselect: "runs.label.preselect",
  author: "runs.label.author",
  kurzprofil: "runs.label.kurzprofil",
}

/** Activity → German title (prefix-matched; a default for the unmapped). */
function activityKey(activityType?: string): CareerKey {
  if (!activityType) return "runs.activity.default"
  if (activityType.startsWith("search")) return "runs.activity.search"
  if (activityType.startsWith("ingest")) return "runs.activity.ingest"
  if (activityType.startsWith("workshop:probe")) return "runs.activity.workshopCard"
  if (activityType.startsWith("workshop")) return "runs.activity.workshop"
  if (activityType.startsWith("application")) return "runs.activity.generate"
  return "runs.activity.default"
}

// ─── Aggregation ────────────────────────────────────────────────────────────────
// The formatters (`fmtEur` / `fmtNum` / `fmtRate` / `fmtWhen`) and the per-step
// `costSplit` live in `../lib/run-trace`, so a second trace surface reads the same
// numbers in the same shape.

const runCost = (r: RunView): number => r.steps.reduce((sum, s) => sum + (s.costUsd ?? 0), 0)

/** True when at least one step on the run carried a provider price (operator view). */
const hasProviderCost = (r: RunView): boolean => r.steps.some((s) => s.costUsd !== undefined)

/** What a just-executed rerun told us, kept beside the step it repeated. */
export interface RerunOutcome {
  ok: boolean
  structural: "valid" | "invalid" | "unchecked"
  detail?: string
  schemaMissing: boolean
}

/** One model's rolled-up performance across every captured call — the D2 "per-model" read. */
export interface ModelRollup {
  model: string
  engine: RunEngine
  calls: number
  tokens: number
  /** Mean latency across the calls that reported one; null when none did. */
  avgLatencyMs: number | null
  /** Summed provider cost; null when this caller may not see provider cost. */
  costUsd: number | null
}

// ─── Copy-to-clipboard (Clipboard API → execCommand fallback) ───────────────────

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // fall through to the execCommand path
  }
  try {
    const ta = document.createElement("textarea")
    ta.value = text
    ta.setAttribute("readonly", "")
    ta.style.position = "fixed"
    ta.style.left = "-9999px"
    document.body.appendChild(ta)
    ta.focus()
    ta.select()
    const ok = document.execCommand("copy")
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

// ─── ViewModel ──────────────────────────────────────────────────────────────────

export class RunsVm implements ViewModel {
  readonly runs: Signal<readonly RunView[] | null> = signal<readonly RunView[] | null>(null)
  readonly rate: Signal<number> = signal(0.92)
  /** Server-declared: may this caller see RAW provider prices? Convenience only — the server
   *  already omitted `costUsd` from the payload if not. */
  readonly providerCost: Signal<boolean> = signal(false)
  /** The models a rerun may target — fetched lazily, the first time anyone opens the control. */
  readonly models: Signal<readonly { id: string; label: string }[]> = signal<readonly { id: string; label: string }[]>([])
  /** `${runId}:${stepIndex}` of the rerun currently in flight, or null. */
  readonly rerunBusy: Signal<string | null> = signal<string | null>(null)
  /** Last rerun outcome per step key — what the caller just learned, kept beside its own step. */
  readonly rerunResults: Signal<Readonly<Record<string, RerunOutcome>>> = signal<Readonly<Record<string, RerunOutcome>>>({})
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly loading: Signal<boolean> = signal(true)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  constructor(i18n: CareerI18n) {
    this.t = i18n.t
    this.renderDeps = [this.runs, this.rate, this.providerCost, this.models, this.rerunBusy, this.rerunResults, this.error, this.loading]
  }

  attach(): () => void {
    void this.load()
    // The rerun model allowlist is a handful of ids and every expanded step offers it, so it is
    // fetched once alongside the runs rather than on a `<summary>` toggle — a disclosure element
    // is not an affordance and must not carry a `data-act` (AX-3).
    void this.loadModels()
    return () => {}
  }

  async load(): Promise<void> {
    this.loading.set(true)
    this.error.set(null)
    try {
      const data = await apiGet<{ runs: unknown[]; usdEurRate: unknown; providerCostVisible: unknown }>("/api/runs")
      const runs = Array.isArray(data.runs) ? data.runs.map(parseRun).filter((r): r is RunView => r !== null) : []
      this.runs.set(runs)
      this.rate.set(typeof data.usdEurRate === "number" && data.usdEurRate > 0 ? data.usdEurRate : 0.92)
      // Trust the payload over the flag: if no step carries a price, there is nothing to show.
      this.providerCost.set(data.providerCostVisible === true && runs.some(hasProviderCost))
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loading.set(false)
    }
  }

  /** Load the rerun model allowlist once per surface mount; a failure degrades, never throws. */
  async loadModels(): Promise<void> {
    if (this.models.value.length > 0) return
    try {
      const data = await apiGet<{ models: unknown }>("/api/runs/models")
      const models = Array.isArray(data.models)
        ? data.models.flatMap((m): { id: string; label: string }[] => {
            const mm = (m ?? {}) as Record<string, unknown>
            const id = str(mm["id"])
            const label = str(mm["label"])
            return id && label ? [{ id, label }] : []
          })
        : []
      this.models.set(models)
    } catch {
      // A failed allowlist fetch leaves the select on the step's own model — the control still works.
    }
  }

  /**
   * Re-execute one captured call. A NEW run is captured server-side, so the list is reloaded
   * afterwards: the rerun must show up in the trace like any other call, not only as a toast.
   */
  async rerun(key: string, runId: string, stepIndex: number, model?: string, instructions?: string): Promise<void> {
    this.rerunBusy.set(key)
    try {
      const out = await apiPost<{ structural?: unknown; structuralError?: unknown; schemaMissing?: unknown }>("/api/runs/rerun", {
        runId,
        stepIndex,
        ...(model ? { model } : {}),
        ...(instructions?.trim() ? { instructions: instructions.trim() } : {}),
      })
      const structural = str(out.structural)
      this.rerunResults.set({
        ...this.rerunResults.value,
        [key]: {
          ok: true,
          structural: structural === "valid" || structural === "invalid" ? structural : "unchecked",
          ...(str(out.structuralError) ? { detail: str(out.structuralError) } : {}),
          schemaMissing: out.schemaMissing === true,
        },
      })
      await this.load()
    } catch (e) {
      this.rerunResults.set({
        ...this.rerunResults.value,
        [key]: { ok: false, structural: "unchecked", detail: e instanceof Error ? e.message : String(e), schemaMissing: false },
      })
    } finally {
      this.rerunBusy.set(null)
    }
  }

  totals(runs: readonly RunView[]): { runs: number; calls: number; tokens: number; credits: number; cost: number } {
    return {
      runs: runs.length,
      calls: runs.reduce((n, r) => n + r.steps.length, 0),
      tokens: runs.reduce((n, r) => n + r.steps.reduce((s, st) => s + (st.promptTokens ?? 0) + (st.completionTokens ?? 0), 0), 0),
      credits: runs.reduce((n, r) => n + r.credits, 0),
      cost: runs.reduce((n, r) => n + runCost(r), 0),
    }
  }

  /**
   * Roll every captured call up BY MODEL — how often each model ran, how many tokens it moved,
   * how fast it answered, and (operator only) what it cost. Busiest model first; this is the
   * "which model is actually doing the work, and how well" read that a per-run list cannot give.
   */
  byModel(runs: readonly RunView[]): ModelRollup[] {
    const acc = new Map<string, { engine: RunEngine; calls: number; tokens: number; latencySum: number; latencyN: number; cost: number; costSeen: boolean }>()
    for (const run of runs) {
      for (const step of run.steps) {
        const entry = acc.get(step.model) ?? { engine: step.engine, calls: 0, tokens: 0, latencySum: 0, latencyN: 0, cost: 0, costSeen: false }
        entry.calls += 1
        entry.tokens += (step.promptTokens ?? 0) + (step.completionTokens ?? 0)
        if (step.latencyMs !== undefined) {
          entry.latencySum += step.latencyMs
          entry.latencyN += 1
        }
        if (step.costUsd !== undefined) {
          entry.cost += step.costUsd
          entry.costSeen = true
        }
        acc.set(step.model, entry)
      }
    }
    return [...acc.entries()]
      .map(([model, e]) => ({
        model,
        engine: e.engine,
        calls: e.calls,
        tokens: e.tokens,
        avgLatencyMs: e.latencyN > 0 ? e.latencySum / e.latencyN : null,
        costUsd: e.costSeen ? e.cost : null,
      }))
      // Busiest first: most calls, then most tokens moved (a tie on calls is broken by actual
      // work, not by alphabet), then the name so the order is stable across renders.
      .sort((a, b) => b.calls - a.calls || b.tokens - a.tokens || a.model.localeCompare(b.model))
  }
}

// ─── Element ────────────────────────────────────────────────────────────────────

export class CareerRuns extends KitElement<RunsVm> {
  protected createViewModel(ctx: KitContext): RunsVm {
    return new RunsVm(ctx.inject(I18nToken))
  }

  private labelChip(vm: RunsVm, label: string): TemplateResult {
    const key = LABEL_KEY[label]
    return html`<span class="run-label" lang="de">${key ? vm.t(key) : label}</span>`
  }

  private metric(vm: RunsVm, labelKey: CareerKey, value: string, hint?: string): TemplateResult {
    return html`
      <div class="run-metric">
        <span class="run-metric-label" lang="de">${vm.t(labelKey)}</span>
        <span class="run-metric-value">${value}</span>
        ${hint ? html`<span class="run-metric-hint" lang="de">${hint}</span>` : nothing}
      </div>
    `
  }

  /**
   * The PROVIDER cost of one step — operator-only. Absent `costUsd` means the server did not
   * send one, so nothing is rendered at all rather than a misleading €0.
   */
  private costBlock(vm: RunsVm, step: RunStepView, rate: number): TemplateResult | typeof nothing {
    if (step.costUsd === undefined) return nothing
    const split = costSplit(step)
    const cached = step.cachedTokens ?? 0
    return html`
      <div class="run-cost">
        <div class="run-cost-head">
          <span class="run-section-label" lang="de">${vm.t("runs.cost.title")}</span>
          <span class="run-cost-rate" lang="de">${vm.t("runs.cost.rate", { rate: fmtRate(rate) })}</span>
        </div>
        ${split
          ? html`
              <div class="run-cost-body">
                <span class="run-cost-item" lang="de">${vm.t("runs.cost.input")} <b>${fmtEur(split.input, rate)}</b></span>
                <span class="run-cost-item" lang="de">${vm.t("runs.cost.output")} <b>${fmtEur(split.output, rate)}</b></span>
                <span class="run-cost-item run-cost-total" lang="de">${vm.t("runs.cost.total")} <b>${fmtEur(step.costUsd, rate)}</b></span>
                <span class="run-cost-note" lang="de">${vm.t("runs.cost.estimated")}${split.cacheAdjusted ? ` · ${vm.t("runs.cost.cacheNote")}` : ""}</span>
              </div>
            `
          : html`<p class="run-cost-free" lang="de">${vm.t("runs.cost.free")}${cached > 0 ? ` · ${vm.t("runs.cacheChip", { n: fmtNum(cached) })}` : ""}</p>`}
      </div>
    `
  }

  /**
   * The per-model rollup — busiest model first. Everyone sees calls / tokens / mean latency
   * (real performance insight); the provider-cost column exists only when provider cost does.
   */
  private modelTable(vm: RunsVm, rows: readonly ModelRollup[], rate: number, providerCost: boolean): TemplateResult {
    return html`
      <section class="runs-models" aria-labelledby="runs-models-title">
        <h2 id="runs-models-title" class="run-section-label" lang="de">${vm.t("runs.models.title")}</h2>
        <ul class="runs-model-list">
          ${repeat(
            rows,
            (r) => r.model,
            (r) => html`
              <li class="runs-model">
                <span class="runs-model-name">${r.model}</span>
                ${engineChip(r.engine, vm.t)}
                <span class="runs-model-figure" lang="de">${vm.t(r.calls === 1 ? "runs.callsOne" : "runs.calls", { n: fmtNum(r.calls) })}</span>
                <span class="runs-model-figure" lang="de">${r.tokens > 0 ? `${fmtNum(r.tokens)} ${vm.t("runs.metric.tokens")}` : "—"}</span>
                <span class="runs-model-figure" lang="de">${r.avgLatencyMs !== null ? vm.t("runs.models.avgLatency", { ms: fmtNum(Math.round(r.avgLatencyMs)) }) : "—"}</span>
                ${providerCost ? html`<span class="runs-model-figure runs-model-cost">${r.costUsd !== null ? fmtEur(r.costUsd, rate) : "—"}</span>` : nothing}
              </li>
            `,
          )}
        </ul>
      </section>
    `
  }

  /**
   * The rerun control (D3/D4) — "barely notable" by construction: a plain disclosure verb at the
   * foot of an already-expanded step. Closed, it is one line of small type; open, it offers the
   * model and an optional steer. Nothing about it competes with the trace it sits under.
   *
   * Only offered when the step actually carries a prompt — a deterministic step has no call to
   * repeat, and offering a button that can only 412 is worse than not offering it.
   */
  private rerunControl(vm: RunsVm, step: RunStepView, key: string): TemplateResult | typeof nothing {
    if (!step.prompt || step.prompt.length === 0) return nothing
    const busy = vm.rerunBusy.value === key
    const outcome = vm.rerunResults.value[key]
    const models = vm.models.value
    const options = models.length > 0 ? models : [{ id: step.model, label: step.model }]
    return html`
      <details class="run-rerun" data-rerun-key=${key}>
        <summary class="run-rerun-summary" lang="de">${vm.t("runs.rerun.open")}</summary>
        <div class="run-rerun-body">
          <p class="run-rerun-hint" lang="de">${vm.t("runs.rerun.hint")}</p>
          <label class="run-rerun-field">
            <span class="run-section-label" lang="de">${vm.t("runs.rerun.model")}</span>
            <select class="run-rerun-model" .value=${step.model}>
              ${repeat(options, (m) => m.id, (m) => html`<option value=${m.id} ?selected=${m.id === step.model}>${m.label}</option>`)}
            </select>
          </label>
          <label class="run-rerun-field">
            <span class="run-section-label" lang="de">${vm.t("runs.rerun.instructions")}</span>
            <textarea class="run-rerun-instructions" rows="2" lang="de" placeholder=${vm.t("runs.rerun.instructionsPlaceholder")} ${seamAutoGrow()}></textarea>
          </label>
          <button type="button" class="run-rerun-go" data-act="rerun-step" ?disabled=${busy} lang="de">
            ${busy ? vm.t("runs.rerun.running") : vm.t("runs.rerun.go")}
          </button>
          ${outcome
            ? html`<p class="run-rerun-outcome ${outcome.ok ? "is-ok" : "is-error"}" role="status" lang="de">
                ${outcome.ok ? vm.t(`runs.rerun.result.${outcome.structural}` as CareerKey) : vm.t("runs.rerun.result.failed")}${outcome.schemaMissing ? ` · ${vm.t("runs.rerun.noSchema")}` : ""}${outcome.detail ? ` — ${outcome.detail}` : ""}
              </p>`
            : nothing}
        </div>
      </details>
    `
  }

  private step(vm: RunsVm, step: RunStepView, index: number, rate: number, runId: string): TemplateResult {
    const cached = step.cachedTokens ?? 0
    return html`
      <details class="run-step">
        <summary class="run-step-summary">
          <span class="run-step-index">#${index + 1}</span>
          ${engineChip(step.engine, vm.t)}
          ${step.label ? this.labelChip(vm, step.label) : nothing}
          <span class="run-step-model">${step.model}</span>
          ${step.reviewed
            ? html`<span
                class=${classMap({ "run-reviewed": true, "is-edited": step.reviewEdited === true })}
                lang="de"
                title=${vm.t(step.reviewEdited ? "runs.reviewed.editedHint" : "runs.reviewed.hint")}
                >${vm.t(step.reviewEdited ? "runs.reviewed.edited" : "runs.reviewed.plain")}</span
              >`
            : nothing}
          ${step.costUsd !== undefined ? html`<span class="run-step-cost">${fmtEur(step.costUsd, rate)}</span>` : nothing}
        </summary>
        <div class="run-step-body">
          ${step.purpose ? html`<p class="run-step-purpose" lang="de">${step.purpose}</p>` : nothing}

          <div class="run-metrics">
            ${this.metric(vm, "runs.metric.input", step.promptTokens != null ? fmtNum(step.promptTokens) : "—", vm.t("runs.metric.tokens"))}
            ${this.metric(vm, "runs.metric.output", step.completionTokens != null ? fmtNum(step.completionTokens) : "—", vm.t("runs.metric.tokens"))}
            ${step.reasoningTokens != null ? this.metric(vm, "runs.metric.reasoning", fmtNum(step.reasoningTokens), vm.t("runs.metric.tokens")) : nothing}
            ${this.metric(vm, "runs.metric.cache", cached > 0 ? fmtNum(cached) : step.cacheHit ? vm.t("runs.metric.yes") : "—")}
            ${this.metric(vm, "runs.metric.latency", step.latencyMs != null ? `${fmtNum(Math.round(step.latencyMs))} ms` : "—")}
          </div>

          ${tokenBar(step)}
          ${this.costBlock(vm, step, rate)}

          <div class="run-params">
            <span class="run-section-label" lang="de">${vm.t("runs.params.title")}</span>
            ${paramGrid(step, vm.t)}
          </div>

          <div class="run-prompts" data-step-prompts>
            <div class="run-prompts-head">
              <span class="run-section-label" lang="de">${vm.t("runs.prompt.title")}${step.promptTruncated ? ` · ${vm.t("runs.prompt.truncatedTag")}` : ""}</span>
              ${step.prompt && step.prompt.length > 0
                ? html`<button type="button" data-act="copy-all-prompt" class="run-copy" lang="de">${vm.t("runs.prompt.copyAll")}</button>`
                : nothing}
            </div>
            ${step.prompt && step.prompt.length > 0
              ? html`
                  ${repeat(step.prompt, (_m, i) => i, (m) => promptBlock(m, vm.t))}
                  ${step.promptTruncated ? html`<p class="run-prompt-note" lang="de">${vm.t("runs.prompt.truncatedNote")}</p>` : nothing}
                `
              : html`<p class="run-prompt-none" lang="de">${vm.t("runs.prompt.none")}</p>`}
          </div>

          ${step.completion
            ? html`
                <details class="run-completion" data-step-completion>
                  <summary class="run-completion-summary" lang="de">${vm.t("runs.completion.title")}${step.completionTruncated ? ` · ${vm.t("runs.completion.truncatedTag")}` : ""}</summary>
                  <div class="run-completion-body">
                    <div class="run-prompts-head">
                      <span class="run-section-label" lang="de">${vm.t("runs.completion.subtitle")}</span>
                      <button type="button" data-act="copy-completion" class="run-copy" lang="de">${vm.t("runs.prompt.copy")}</button>
                    </div>
                    <pre class="run-completion-pre">${step.completion}</pre>
                    ${step.completionTruncated ? html`<p class="run-prompt-note" lang="de">${vm.t("runs.completion.truncatedNote")}</p>` : nothing}
                  </div>
                </details>
              `
            : nothing}

          ${step.contextNote
            ? html`
                <div class="run-context">
                  <span class="run-section-label" lang="de">${vm.t("runs.why")}</span>
                  <p class="run-context-note" lang="de">${step.contextNote}</p>
                </div>
              `
            : nothing}

          ${this.rerunControl(vm, step, `${runId}:${index}`)}
        </div>
      </details>
    `
  }

  private run(vm: RunsVm, run: RunView, rate: number, open: boolean): TemplateResult {
    const n = run.steps.length
    return html`
      <details class="run" ?open=${open}>
        <summary class="run-summary">
          <div class="run-summary-head">
            <h3 class="run-title" lang="de">${vm.t(activityKey(run.activityType))}</h3>
            <span class="run-when">${fmtWhen(run.createdAt)}</span>
          </div>
          <div class="run-summary-meta">
            <span class="run-calls" lang="de">${vm.t(n === 1 ? "runs.callsOne" : "runs.calls", { n: fmtNum(n) })}</span>
            ${hasProviderCost(run) ? html`<span class="run-provider-cost" lang="de">${fmtEur(runCost(run), rate)}</span>` : nothing}
            <span class="run-total-cost" lang="de">${vm.t(run.credits === 1 ? "runs.creditsOne" : "runs.credits", { n: fmtNum(run.credits) })}</span>
          </div>
        </summary>
        <div class="run-steps">
          ${repeat(run.steps, (_s, i) => `${run.id}:${i}`, (s, i) => this.step(vm, s, i, rate, run.id))}
        </div>
      </details>
    `
  }

  protected render(vm: RunsVm): TemplateResult {
    const runs = vm.runs.value
    const rate = vm.rate.value
    const err = vm.error.value

    const body = ((): TemplateResult => {
      if (err) {
        return html`
          <div class="runs-error-box">
            <p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>
            <button type="button" data-act="reload-runs" lang="de">${vm.t("runs.retry")}</button>
          </div>
        `
      }
      if (vm.loading.value && runs === null) {
        return html`<div class="runs-list" aria-busy="true" aria-label=${vm.t("runs.loading")}>${skeletonRows(3)}</div>`
      }
      if (!runs || runs.length === 0) {
        return html`<p class="surface-empty" lang="de">${vm.t("runs.empty")}</p>`
      }
      const t = vm.totals(runs)
      const providerCost = vm.providerCost.value
      return html`
        <div class="runs-ledger">
          ${this.metric(vm, "runs.ledger.runs", fmtNum(t.runs))}
          ${this.metric(vm, "runs.ledger.calls", fmtNum(t.calls))}
          ${this.metric(vm, "runs.ledger.tokens", fmtNum(t.tokens))}
          ${this.metric(vm, "runs.ledger.credits", fmtNum(t.credits), vm.t("runs.credits.hint"))}
          ${providerCost ? this.metric(vm, "runs.ledger.total", fmtEur(t.cost, rate), vm.t("runs.cost.rate", { rate: fmtRate(rate) })) : nothing}
        </div>
        ${this.modelTable(vm, vm.byModel(runs), rate, providerCost)}
        <section class="runs-list" aria-labelledby="runs-history-title">
          <h2 id="runs-history-title" class="run-section-label" lang="de">${vm.t("runs.history.title")}</h2>
          ${repeat(runs, (r) => r.id, (r, i) => this.run(vm, r, rate, i === 0))}
        </section>
      `
    })()

    return html`
      <section class="surface runs" aria-labelledby="runs-title">
        <h1 id="runs-title" class="surface-title" lang="de">${vm.t("surface.laeufe.title")}</h1>
        <p class="surface-lead" lang="de">${vm.t("surface.laeufe.lead")}</p>
        ${body}
      </section>
    `
  }

  protected override actions(vm: RunsVm): ActionMap {
    const feedback = async (btn: HTMLElement, text: string): Promise<void> => {
      const ok = await copyToClipboard(text)
      const prev = btn.textContent
      btn.textContent = ok ? vm.t("runs.prompt.copied") : vm.t("runs.prompt.copyFailed")
      setTimeout(() => {
        btn.textContent = prev
      }, 1800)
    }
    return {
      "reload-runs": () => void vm.load(),
      "rerun-step": (_payload, event) => {
        const host = (event.target as HTMLElement).closest("[data-rerun-key]")
        const key = host?.getAttribute("data-rerun-key")
        if (!host || !key) return
        const [runId, indexRaw] = [key.slice(0, key.lastIndexOf(":")), key.slice(key.lastIndexOf(":") + 1)]
        const stepIndex = Number(indexRaw)
        if (!runId || !Number.isInteger(stepIndex)) return
        const model = host.querySelector("select")?.value
        const instructions = host.querySelector("textarea")?.value
        void vm.rerun(key, runId, stepIndex, model, instructions)
      },
      "copy-prompt": (_payload, event) => {
        const btn = event.target as HTMLElement
        const pre = btn.closest(".run-prompt-msg")?.querySelector("pre")
        if (pre) void feedback(btn, pre.textContent ?? "")
      },
      "copy-all-prompt": (_payload, event) => {
        const btn = event.target as HTMLElement
        const container = btn.closest("[data-step-prompts]")
        const pres = container ? [...container.querySelectorAll("pre")] : []
        const text = pres.map((p) => p.textContent ?? "").join("\n\n")
        void feedback(btn, text)
      },
      "copy-completion": (_payload, event) => {
        const btn = event.target as HTMLElement
        const pre = btn.closest("[data-step-completion]")?.querySelector("pre")
        if (pre) void feedback(btn, pre.textContent ?? "")
      },
    }
  }
}
